/**
 * `payments` — `POST /v1/orders/{id}/payments` (`API §6.20.9`) e `POST /v1/webhooks/psp/pix`
 * (`API §6.20.10`, `§7.1`).
 *
 * Duas superficies muito diferentes no mesmo arquivo, e vale dizer por que:
 *
 * | | quem chama | o que o corpo pode fazer |
 * | --- | --- | --- |
 * | `CreatePaymentRequest` | o cliente, com sessao | escolher **meio** e **troco**. Nada mais |
 * | `PspPixWebhook` | o PSP, sem sessao | **nada**. Ele so diz *"olha para o txid X"* |
 *
 * ## O corpo do cliente nao tem valor, e o do PSP tem — e nenhum dos dois e usado
 *
 * `CreatePaymentRequest` nao tem campo monetario nenhum: o valor da cobranca e o
 * `order.totalCents`, que veio da cotacao. `changeForCents` **e** dinheiro, mas nao e valor da
 * cobranca — e quanto o cliente vai entregar em especie, e serve para o balconista levar troco
 * (INV-O10).
 *
 * O webhook, ao contrario, **carrega** `valor` — porque o PSP o manda, e recusar o campo faria
 * o parse falhar num payload legitimo. O schema o aceita e o codigo o ignora:
 *
 * > `API §6.20.10`, regra 2: *"Nunca confiar no valor do webhook. O handler enfileira e o
 * > worker faz `GET /cob/{txid}` na API do PSP para confirmar valor e status."*
 *
 * Ele nao esta aqui para ser lido: esta aqui para ser **gravado cru** em `payment_event` e
 * comparado com o que a reconsulta trouxer. Divergencia e `PIX_AMOUNT_MISMATCH` + incidente P1,
 * nao um pedido pago.
 *
 * ## Por que o webhook e `.passthrough()` e todo o resto e `.strict()`
 *
 * `.strict()` num corpo de terceiro seria auto-sabotagem: o PSP acrescenta um campo na versao
 * seguinte da API dele e a nossa rota passa a devolver `422` para toda notificacao, incluindo a
 * de um pedido pago de verdade. O que protege a rota nao e a recusa de campo desconhecido — e a
 * assinatura, que roda **antes** do parse, e a reconsulta, que roda depois.
 */
import { z } from 'zod';

import { PaymentMethodSchema, PaymentStatusSchema } from '../enums';
import { InstantSchema, MoneySchema, NonNegativeMoneySchema, Uuidv7Schema } from '../common';

// ---------------------------------------------------------------------------
// Constantes de protocolo — nenhuma e configuravel (CONTRACTS §10.3)
// ---------------------------------------------------------------------------

/**
 * **30 minutos** de validade do QR dinamico (`CONTRACTS §2.3`, auditoria 03 §2.2).
 *
 * Nao e chave de perfil, e a lista de `IMPLANTACAO §2.9` diz isso em uma linha: *"[P] TTL do Pix
 * (30 min) e da cotacao (10 min) NAO sao configuraveis"*. Uma farmacia com 5 min quebraria o
 * cliente que abre o app do banco; uma com 2 h prenderia a reserva de estoque muito depois de o
 * TTL de 20 min dela ter vencido.
 */
export const PIX_TTL_MINUTES = 30;

/**
 * Tolerancia de relogio da assinatura do webhook: **5 min** (`API §7.1`, `§6.20.10` regra 1).
 * Fora da janela ⇒ `401 WEBHOOK_TIMESTAMP_SKEW`, que e o que impede um corpo assinado
 * legitimamente ontem de ser reenviado hoje por quem o capturou.
 */
export const WEBHOOK_SKEW_TOLERANCE_SECONDS = 300;

/** `API §6.20.10` regra 6 — a rede de seguranca reconsulta cobranca `PENDENTE` ha mais de 2 min. */
export const PIX_RECONCILE_AFTER_SECONDS = 120;

/** Ritmo de polling que o **servidor** dita ao cliente (`API §6.20.9`, `pollAfterSeconds`). */
export const PIX_POLL_AFTER_SECONDS = 5;

// ---------------------------------------------------------------------------
// Requisicao — POST /v1/orders/{id}/payments
// ---------------------------------------------------------------------------

/**
 * Mesma uniao discriminada de `CreateOrderRequest.payment`, e de proposito: o meio escolhido no
 * checkout e o meio cobrado, e dois schemas com formas diferentes para a mesma escolha
 * produziriam um pedido `DINHEIRO_ENTREGA` com cobranca `PIX` sem que nada reclamasse.
 */
export const CreatePaymentRequestSchema = z.discriminatedUnion('method', [
  z.object({ method: z.literal('PIX') }).strict(),
  z
    .object({
      method: z.literal('DINHEIRO_ENTREGA'),
      /** Para quanto levar troco. `null` = "não preciso de troco" (`API §6.20.7`). */
      changeForCents: z.number().int().nonnegative().nullable(),
    })
    .strict(),
  z
    .object({ method: z.literal('MAQUININHA_ENTREGA'), cardKind: z.enum(['DEBIT', 'CREDIT']) })
    .strict(),
]);
export type CreatePaymentRequest = z.infer<typeof CreatePaymentRequestSchema>;

// ---------------------------------------------------------------------------
// Resposta
// ---------------------------------------------------------------------------

/**
 * O bloco Pix. `brCode` e o copia-e-cola; **a imagem do QR e gerada no cliente** e nunca
 * armazenada (`API §6.20.9`, comentario de `payment.brCode` no `DATA_MODEL`). Guardar PNG de QR
 * seria guardar o mesmo dado duas vezes, em formato que ninguem consegue conferir.
 */
export const PixChargeSchema = z.object({
  /** 26–35 caracteres, formato BACEN (`API §6.20.9`). */
  txid: z.string().min(26).max(35),
  brCode: z.string().min(1),
  expiresAt: InstantSchema,
  endToEndId: z.string().nullable(),
  /**
   * A chave Pix da **filial que vai atender** — evita split (auditoria 03 §2.5). Em perfil
   * `CENTRALIZADO` e a chave central, e o campo continua nomeando a filial de destino: o que
   * muda e para onde o dinheiro cai, nao quem atende.
   */
  receiverStoreId: Uuidv7Schema,
});
export type PixCharge = z.infer<typeof PixChargeSchema>;

export const OnDeliveryChargeSchema = z.object({
  changeForCents: z.number().int().nonnegative().nullable(),
  /**
   * O teto que valeu para **este** titular, ja resolvido pela escada de `SECURITY §8.5`. A UI o
   * usa para dizer o limite antes de o cliente esbarrar nele — e ele e por degrau, entao um
   * numero fixo na tela mentiria para metade dos clientes.
   */
  maxAllowedCents: z.number().int().nonnegative(),
});
export type OnDeliveryCharge = z.infer<typeof OnDeliveryChargeSchema>;

export const PaymentResponseSchema = z.object({
  id: Uuidv7Schema,
  orderId: Uuidv7Schema,
  method: PaymentMethodSchema,
  status: PaymentStatusSchema,
  amount: NonNegativeMoneySchema,
  pix: PixChargeSchema.nullable(),
  onDelivery: OnDeliveryChargeSchema.nullable(),
  /** O servidor dita o ritmo do polling; o cliente nao escolhe (`API §6.20.9`). */
  pollAfterSeconds: z.number().int().positive(),
});
export type PaymentResponse = z.infer<typeof PaymentResponseSchema>;

/** `GET /v1/orders/{id}/payments/{paymentId}` — o polling do cliente. Mesmo corpo. */
export const PaymentStatusResponseSchema = PaymentResponseSchema;
export type PaymentStatusResponse = z.infer<typeof PaymentStatusResponseSchema>;

// ---------------------------------------------------------------------------
// Webhook de entrada — POST /v1/webhooks/psp/pix
// ---------------------------------------------------------------------------

/** Cabecalhos que `API §6.20.10` declara **obrigatorios**. Os dois, sempre. */
export const PSP_SIGNATURE_HEADER = 'x-psp-signature';
export const PSP_DELIVERY_HEADER = 'x-psp-delivery-id';

/**
 * Uma notificacao do PSP. `valor` e string porque e assim que o BACEN o serializa (`"87.40"`) —
 * e porque **ele nao e lido**: converter para centavos aqui daria a impressao de que o numero
 * entra em alguma conta. Ele entra em uma so: a comparacao com o que a reconsulta trouxer.
 */
export const PspPixNotificationSchema = z
  .object({
    txid: z.string().min(1).max(35),
    endToEndId: z.string().min(1).optional(),
    valor: z.string().min(1).optional(),
    horario: z.string().min(1).optional(),
  })
  .passthrough();
export type PspPixNotification = z.infer<typeof PspPixNotificationSchema>;

/** O envelope. Ver o cabecalho: `passthrough`, e a razao esta escrita la. */
export const PspPixWebhookSchema = z
  .object({
    pix: z.array(PspPixNotificationSchema).min(1).max(100),
  })
  .passthrough();
export type PspPixWebhook = z.infer<typeof PspPixWebhookSchema>;

/**
 * O que a rota devolve. `204` sem corpo e o contrato (`API §6.20.10`), e o corpo existe aqui so
 * para o OpenAPI ter o que descrever no `200` que **nao** e usado.
 *
 * Nao ha `PspPixWebhookResponse` com contagem de eventos aceitos, e a ausencia e deliberada:
 * qualquer numero na resposta diria ao remetente se o `txid` que ele inventou existe aqui. Um
 * webhook forjado nao pode virar oraculo de enumeracao (`SECURITY §7.4`).
 */
export const WEBHOOK_ACCEPTED_STATUS = 204;

/**
 * A cobranca vista pelo PSP na **reconsulta** — o unico dado que confirma pagamento
 * (`DOMAIN §BC-09` invariante i, `DATA_MODEL` R5, `CONTRACTS §2.1`).
 *
 * Nao e resposta de rota nossa: e a forma que `PaymentGatewayPort.consultar` devolve, e ela
 * mora no contrato porque o adaptador real (integracao futura, DP-1/DP-4) tera de produzi-la
 * exatamente assim para trocar de lugar com o falso.
 */
export const PSP_CHARGE_STATUS_VALUES = ['ATIVA', 'CONCLUIDA', 'REMOVIDA', 'EXPIRADA'] as const;
export const PspChargeStatusSchema = z.enum(PSP_CHARGE_STATUS_VALUES);
export type PspChargeStatus = z.infer<typeof PspChargeStatusSchema>;

export const PspChargeViewSchema = z.object({
  txid: z.string().min(1).max(35),
  status: PspChargeStatusSchema,
  /** O valor **do PSP**, em centavos. E este que conta, e nunca o do corpo do webhook. */
  amount: MoneySchema,
  paidAt: InstantSchema.nullable(),
  endToEndId: z.string().nullable(),
  payerTaxIdMasked: z.string().nullable(),
});
export type PspChargeView = z.infer<typeof PspChargeViewSchema>;
